// Catalog query hooks. Cache keys include the active runtime store scope so
// pricing and brand switches invalidate cleanly.
import { useQuery, useQueries, keepPreviousData } from '@tanstack/react-query';
import * as catalog from '@core/services/catalogService';
import {
  selectActiveChannelId,
  selectPlatformSlug,
  useStoreContextStore,
} from '@core/store/storeContextStore';

const CATALOG_KEY = 'catalog';

/**
 * @param {{ categoryId?: string, page?: number, limit?: number, search?: string, sort?: string, enabled?: boolean }} [params]
 */
export function useProducts(params = {}) {
  const accountId = useStoreContextStore((s) => s.accountId);
  const locationId = useStoreContextStore((s) => s.locationId);
  const channelId = useStoreContextStore(selectActiveChannelId);
  const brandId = useStoreContextStore((s) => s.brandId);
  const companyId = useStoreContextStore((s) => s.companyId);
  const platformSlug = useStoreContextStore(selectPlatformSlug);
  const priceField = useStoreContextStore((s) => s.priceField);

  return useQuery({
    queryKey: [
      CATALOG_KEY,
      'list',
      { accountId, locationId, channelId, brandId, companyId, platformSlug, priceField, ...params },
    ],
    queryFn: ({ signal }) =>
      catalog.listProducts({ accountId, locationId, channelId, priceField, signal, ...params }),
    enabled: params.enabled !== false,
  });
}

/**
 * Fetches products for multiple category IDs in parallel and merges results.
 * Use this when clicking a parent category that has sub/sub-sub children.
 *
 * @param {{ categoryIds?: string[], page?: number, limit?: number, search?: string, sort?: string, enabled?: boolean }} [params]
 */
export function useProductsByCategories(params = {}) {
  const accountId = useStoreContextStore((s) => s.accountId);
  const locationId = useStoreContextStore((s) => s.locationId);
  const channelId = useStoreContextStore(selectActiveChannelId);
  const brandId = useStoreContextStore((s) => s.brandId);
  const companyId = useStoreContextStore((s) => s.companyId);
  const platformSlug = useStoreContextStore(selectPlatformSlug);
  const priceField = useStoreContextStore((s) => s.priceField);
  // Sort the IDs so the query key is stable regardless of order
  const sortedIds = [...(params.categoryIds ?? [])].sort();

  return useQuery({
    queryKey: [
      CATALOG_KEY,
      'list-multi',
      {
        accountId,
        locationId,
        channelId,
        brandId,
        companyId,
        platformSlug,
        priceField,
        categoryIds: sortedIds,
        page: params.page,
        search: params.search,
        foodType: params.foodType,
      },
    ],
    queryFn: ({ signal }) =>
      catalog.listProductsByCategories({
        accountId,
        locationId,
        channelId,
        priceField,
        signal,
        ...params,
        categoryIds: sortedIds,
      }),
    enabled: params.enabled !== false,
  });
}

/**
 * @param {string | undefined} id
 */
export function useProduct(id) {
  const accountId = useStoreContextStore((s) => s.accountId);
  const locationId = useStoreContextStore((s) => s.locationId);
  const channelId = useStoreContextStore(selectActiveChannelId);
  const brandId = useStoreContextStore((s) => s.brandId);
  const companyId = useStoreContextStore((s) => s.companyId);
  const platformSlug = useStoreContextStore(selectPlatformSlug);
  const priceField = useStoreContextStore((s) => s.priceField);

  return useQuery({
    queryKey: [
      CATALOG_KEY,
      'detail',
      id,
      { accountId, locationId, channelId, brandId, companyId, platformSlug, priceField },
    ],
    queryFn: ({ signal }) =>
      catalog.getProduct(id, { accountId, locationId, channelId, priceField, signal }),
    enabled: Boolean(id),
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useCategories(params = {}) {
  const accountId = useStoreContextStore((s) => s.accountId);
  const locationId = useStoreContextStore((s) => s.locationId);
  const brandId = useStoreContextStore((s) => s.brandId);
  const companyId = useStoreContextStore((s) => s.companyId);
  const platformSlug = useStoreContextStore(selectPlatformSlug);
  const { limit = 100, ...rest } = params;
  return useQuery({
    queryKey: [
      CATALOG_KEY,
      'categories',
      { accountId, locationId, brandId, companyId, platformSlug, limit, ...rest },
    ],
    queryFn: ({ signal }) =>
      catalog.listCategories({ accountId, locationId, limit, signal, ...rest }),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/**
 * @param {{ page?: number, limit?: number, enabled?: boolean }} [params]
 */
export function useCategoryPage(params = {}) {
  const accountId = useStoreContextStore((s) => s.accountId);
  const locationId = useStoreContextStore((s) => s.locationId);
  const brandId = useStoreContextStore((s) => s.brandId);
  const companyId = useStoreContextStore((s) => s.companyId);
  const platformSlug = useStoreContextStore(selectPlatformSlug);

  return useQuery({
    queryKey: [
      CATALOG_KEY,
      'categories',
      'page',
      { accountId, locationId, brandId, companyId, platformSlug, ...params },
    ],
    queryFn: ({ signal }) => catalog.listCategoryPage({ accountId, locationId, signal, ...params }),
    placeholderData: keepPreviousData,
    enabled: params.enabled !== false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * @param {string[]} ids
 * @param {{ enabled?: boolean }} [options]
 */
export function useProductsByIds(ids = [], options = {}) {
  const accountId = useStoreContextStore((s) => s.accountId);
  const locationId = useStoreContextStore((s) => s.locationId);
  const channelId = useStoreContextStore(selectActiveChannelId);
  const brandId = useStoreContextStore((s) => s.brandId);
  const companyId = useStoreContextStore((s) => s.companyId);
  const platformSlug = useStoreContextStore(selectPlatformSlug);
  const priceField = useStoreContextStore((s) => s.priceField);

  return useQueries({
    queries: ids.map((id) => ({
      queryKey: [
        CATALOG_KEY,
        'detail',
        id,
        { accountId, locationId, channelId, brandId, companyId, platformSlug, priceField },
      ],
      queryFn: ({ signal }) =>
        catalog.getProduct(id, { accountId, locationId, channelId, priceField, signal }),
      enabled: options.enabled !== false && Boolean(id),
      staleTime: 5 * 60 * 1000,
    })),
  });
}
